import { useSchemaOrg } from '@unhead/schema-org/react';

/**
 * @type {import('$types/components/partials/landing').LandingSchema}
 */

const LandingSchema = ({ page, url }) => {
    if (!page) {
        useSchemaOrg([], { tagPosition: 'head' });
        return null;
    }

    // Use custom schema markup from CMS if provided
    if (page.schemaMarkup) {
        try {
            const customSchema = JSON.parse(page.schemaMarkup);
            useSchemaOrg(Array.isArray(customSchema) ? customSchema : [customSchema], { tagPosition: 'head' });
        } catch {
            useSchemaOrg([], { tagPosition: 'head' });
        }
        return null;
    }

    const schemas = [];

    // WebPage Schema for landing pages
    const webPageSchema = {
        '@type': 'WebPage',
        name: page.title,
        url: url,
    };

    // Add description if available
    if (page.searchDescription) {
        webPageSchema.description = page.searchDescription;
    } else if (page.listingText) {
        webPageSchema.description = page.listingText;
    }

    // Add primary image - prefer cover media, fallback to listing image
    if (page.coverMedia?.image?.src) {
        webPageSchema.image = {
            '@type': 'ImageObject',
            url: page.coverMedia.image.src,
        };
    } else if (page.listingImage?.src) {
        webPageSchema.image = {
            '@type': 'ImageObject',
            url: page.listingImage.src,
        };
    }

    // Publisher (RealEstateAgent)
    webPageSchema.publisher = {
        '@type': 'RealEstateAgent',
        name: 'Beauchamp Estates',
        url: url?.split('/').slice(0, 3).join('/')
    };

    // Date published
    if (page.publicationDate) {
        webPageSchema.datePublished = page.publicationDate;
    } else if (page.firstPublishedAt) {
        webPageSchema.datePublished = page.firstPublishedAt;
    }

    // Date modified
    if (page.lastPublishedAt) {
        webPageSchema.dateModified = page.lastPublishedAt;
    } else if (page.lastModified) {
        webPageSchema.dateModified = page.lastModified;
    }

    // Main entity of page (canonical URL)
    if (page.canonicalUrl) {
        webPageSchema.mainEntityOfPage = {
            '@type': 'WebPage',
            '@id': page.canonicalUrl
        };
    } else if (url) {
        webPageSchema.mainEntityOfPage = {
            '@type': 'WebPage',
            '@id': url
        };
    }

    // Add breadcrumb if parent exists
    if (page.parent) {
        webPageSchema.breadcrumb = {
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: url?.split('/').slice(0, 3).join('/')
                }
            ]
        };

        if (page.parent.title) {
            webPageSchema.breadcrumb.itemListElement.push({
                '@type': 'ListItem',
                position: 2,
                name: page.parent.title,
                item: page.parent.relativeUrl
                    ? `${url?.split('/').slice(0, 3).join('/')}${page.parent.relativeUrl}`
                    : url?.split('/').slice(0, 3).join('/')
            });
        }

        webPageSchema.breadcrumb.itemListElement.push({
            '@type': 'ListItem',
            position: webPageSchema.breadcrumb.itemListElement.length + 1,
            name: page.title,
            item: url
        });
    }

    // Add video if available
    if (page.coverMedia?.video?.src) {
        webPageSchema.video = {
            '@type': 'VideoObject',
            contentUrl: page.coverMedia.video.src,
            name: page.title
        };

        // Add thumbnail if available
        if (page.coverMedia.video.poster?.url) {
            webPageSchema.video.thumbnailUrl = page.coverMedia.video.poster.url;
        }

        // Add description if we have it
        if (webPageSchema.description) {
            webPageSchema.video.description = webPageSchema.description;
        }
    }

    schemas.push(webPageSchema);

    useSchemaOrg(schemas, { tagPosition: 'head' });

    return null;
};

export default LandingSchema;
